// Routine names for people.
//
// A routine's `name` is a machine id the agent (or Lattice) chose -
// "remind-hmgcc-apply", "nag-lseg-email-data", "assistant-inbox-watcher".
// It is also load-bearing: the personality check-in is re-created by
// name, and agents dedupe on it (until 25 Oct 2026 the schedules named for
// a person, below, were also deleted by their prefix).
// So: display only. Never send a display name back in a mutation.
//
// Acronyms and proper nouns are recovered from the routine's own prompt
// ("…finish the LSEG email data" → "LSEG"), then from a short list and
// a no-vowels rule ("hmgcc" → "HMGCC"). Removed routines have no prompt,
// so their names are best effort.
//
// No `@/` imports so the self-test bundles standalone.

import { humanizeSlug, isLikelyAcronym } from "./format";

export interface RoutineNameParts {
  /** "Remind" for "remind-…" routines; null otherwise. */
  prefix: string | null;
  /** The rest, humanised ("HMGCC apply"). */
  label: string;
  /** Managed by Lattice or the runtime, not set up by the agent for you. */
  system: boolean;
  /** "reminder" | "routine" - what the activity feed calls it. */
  noun: "reminder" | "routine";
  /** A schedule named for a person: `prefix` is their name ("Ray Dalio"). */
  person: boolean;
}

// "persona-<slug≤24>-<sha1(slug)[:5]>--<suffix>": the names the old person
// projects gave their scheduled takes (until 25 Oct 2026). No new ones are
// made, but those already set up still run, so they are still read.
const NAMED_FOR_RE = /^persona-(.+)-[0-9a-f]{5}--(.+)$/;
const VERB_PREFIX_RE = /^(remind|nag|pester)[-_](.+)$/;

// Consonant pairs an English word can start with ("tsar", "psalm"). A
// short token that starts with two other consonants is almost certainly
// an abbreviation ("lseg", "wte", "nvda") - a bolder guess than
// format's default, fine for routine names because the prompt, when we
// have it, overrides it.
const ONSETS = new Set([
  "bl", "br", "ch", "cl", "cr", "cz", "dr", "dw", "fl", "fr", "gh", "gl", "gn", "gr", "kh", "kn", "mn", "ph", "pl", "pn",
  "pr", "ps", "pt", "qu", "rh", "sc", "sh", "sk", "sl", "sm", "sn", "sp", "sq", "st", "sv", "sw", "th", "tr", "ts", "tw", "wh", "wr",
]);

/** The acronym guess used for routine names. */
export function looksLikeAcronym(lower: string): boolean {
  if (isLikelyAcronym(lower)) return true;
  return /^[a-z]{3,5}$/.test(lower) && /^[^aeiouy]{2}/.test(lower) && !ONSETS.has(lower.slice(0, 2));
}

/**
 * Routines Lattice or the runtime manages (inbox watcher, heartbeat,
 * check-in, skill review). On a workspace on the one schedules store the
 * server says so on the row (`system`: the platform jobs still on gateway
 * cron, docs/tasks/schedules-unified.md §8): that flag wins over the name.
 */
export function isSystemRoutine(name: string | null | undefined, system?: boolean | null): boolean {
  if (typeof system === "boolean") return system;
  const n = (name ?? "").trim().toLowerCase();
  return (
    n === "lattice-checkin" ||
    n.endsWith("-inbox-watcher") ||
    /^heartbeat(?:-|$)/.test(n) ||
    /^skill-collection-review(?:-|$)/.test(n)
  );
}

function capitalizeFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export interface RoutineNameOptions {
  /**
   * Drop a trailing weekday token ("GResearch demo Tue" -> "GResearch
   * demo"). Pass it where the routine's own day is shown beside the name
   * (a one-off's date, a weekly schedule): the agent's token is a label,
   * not a fact, and often not the day it fires ("remind-…-tue" fired on
   * Wed 30 Sep).
   */
  dropTrailingDay?: boolean;
}

const TRAILING_DAY_RE =
  /\s+(?:mon|tues?|wed|thu(?:rs?)?|fri|sat|sun|monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/i;

/** "GResearch demo Tue" -> "GResearch demo"; a lone weekday stays. */
function withoutTrailingDay(label: string): string {
  const cut = label.replace(TRAILING_DAY_RE, "");
  return cut && cut !== label ? cut : label;
}

/**
 * Possessives the slug lost, recovered from the prompt: "Jay personal
 * account" + "…Jay's personal account…" -> "Jay's personal account".
 */
function withPossessives(label: string, prompt: string | null | undefined): string {
  if (!prompt) return label;
  const words = label.split(" ");
  for (let i = 0; i < words.length - 1; i++) {
    const [a, b] = [words[i], words[i + 1]];
    if (!/^[\p{L}\d]+$/u.test(a) || !/^[\p{L}\d]+$/u.test(b)) continue;
    if (new RegExp(`(?:^|[^\\p{L}\\d])${a}['’]s ${b}(?:$|[^\\p{L}\\d])`, "iu").test(prompt)) words[i] = `${a}'s`;
  }
  return words.join(" ");
}

/** The pieces of a routine name; see routineDisplayName. */
export function routineNameParts(
  name: string | null | undefined,
  prompt?: string | null,
  opts: RoutineNameOptions = {},
): RoutineNameParts {
  const parts = rawNameParts(name, prompt);
  if (parts.system) return parts;
  let label = withPossessives(parts.label, prompt);
  if (opts.dropTrailingDay) label = withoutTrailingDay(label);
  return { ...parts, label };
}

function rawNameParts(name: string | null | undefined, prompt?: string | null): RoutineNameParts {
  const raw = (name ?? "").trim();
  const n = raw.toLowerCase();
  const system = isSystemRoutine(raw);
  const plain = (label: string, noun: RoutineNameParts["noun"] = "routine"): RoutineNameParts => ({
    prefix: null, label, system, noun, person: false,
  });
  if (!raw) return plain("Untitled schedule");
  if (n.endsWith("-inbox-watcher")) return plain("Inbox watcher");
  if (n === "lattice-checkin") return plain("Check-in");
  if (/^heartbeat(?:-|$)/.test(n)) return plain("Heartbeat");
  if (/^skill-collection-review(?:-|$)/.test(n)) return plain("Skill collection review");
  const named = NAMED_FOR_RE.exec(n);
  if (named) {
    const who = humanizeSlug(named[1], prompt, { titleCase: true });
    return { prefix: who, label: humanizeSlug(named[2], prompt, { acronyms: looksLikeAcronym }), system, noun: "routine", person: true };
  }
  const verb = VERB_PREFIX_RE.exec(n);
  if (verb && raw === n) {
    return {
      prefix: capitalizeFirst(verb[1]),
      label: humanizeSlug(verb[2], prompt, { acronyms: looksLikeAcronym }),
      system,
      noun: "reminder",
      person: false,
    };
  }
  return plain(humanizeSlug(raw, prompt, { acronyms: looksLikeAcronym }));
}

/**
 * "remind-hmgcc-apply" + a prompt mentioning HMGCC → "Remind: HMGCC apply";
 * "nag-lseg-email-data" → "Nag: LSEG email data"; "oss-model-watch" →
 * "OSS model watch"; "<t>-inbox-watcher" → "Inbox watcher";
 * "lattice-checkin" → "Check-in"; "heartbeat-<t>" → "Heartbeat";
 * "persona-ray-dalio-3fa2c--daily-take" → "Ray Dalio: Daily take".
 * A name that already has spaces or capitals is kept as typed.
 */
export function routineDisplayName(
  name: string | null | undefined,
  prompt?: string | null,
  opts: RoutineNameOptions = {},
): string {
  const parts = routineNameParts(name, prompt, opts);
  return parts.prefix ? `${parts.prefix}: ${parts.label}` : parts.label;
}
